import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import MenuItem from '@mui/material/MenuItem'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */
import useStyles from 'features/setting/default-users/career-lines/style'

/** API */
import CareerLineApi from 'api/setting/default-users/career-line.api'

/** UTILS || SLICE */
import { submitModal, unSubmitModal, resetModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextSearch from 'component/Input/InputTextSearch'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import PageModal from 'new-components/modal/page-modal'
import TextInput from 'new-components/inputs/text-input'
import SwitchButton from 'new-components/buttons/switch-button'
import { Box } from '@mui/material'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  CAREER_LINE_NAME: ``,
  IS_DUPLICATE_CAREER_LINE_NAME: ``,
  CAREER_LINE_NAME_STRING_BASE: ``,
  CAREER_LINE_NAME_STRING_EMPTY: ``,
  CAREER_LINE_NAME_MAXIMUM_LENGTH: ``,
  CAREER_LINE_NAME_ANY_REQUIRED: ``,

  CAREER_LINE_NAME_EN: ``,
  IS_DUPLICATE_CAREER_LINE_NAME_EN: ``,
  CAREER_LINE_NAME_EN_STRING_BASE: ``,
  CAREER_LINE_NAME_EN_STRING_EMPTY: ``,
  CAREER_LINE_NAME_EN_MAXIMUM_LENGTH: ``,
  CAREER_LINE_NAME_EN_ANY_REQUIRED: ``
}

export default function CareerLines() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [careerLines, setCareerLines] = useState([])

  const [careerLineId, setCareerLineId] = useState(Number)
  const [careerLineName, setCareerLineName] = useState('')
  const [careerLineNameEn, setCareerLineNameEn] = useState('')
  const [careerLineStatus, setCareerLineStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>('success')
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)

  const loadData = useCallback(async () => {
    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await CareerLineApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setCareerLines(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const submit = () => {
    if (!careerLineName) return setErrorMessage({ ...errorMessage, ...{ CAREER_LINE_NAME: t('CAREER_LINE.MESSAGE.CAREER_LINE_NAME') } })

    dispatch(submitModal())
    CareerLineApi.create(careerLineName, careerLineNameEn)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('CAREER_LINE.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CAREER_LINE.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CAREER_LINE.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {
    if (!careerLineName) return setErrorMessage({ ...errorMessage, ...{ CAREER_LINE_NAME: t(`CAREER_LINE.MESSAGE.CAREER_LINE_NAME`) } })

    dispatch(submitModal())
    CareerLineApi.update(careerLineId, {
      careerLineName,
      careerLineNameEn,
      status: careerLineStatus
    })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`CAREER_LINE.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CAREER_LINE.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CAREER_LINE.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setCareerLineId(0)
    setCareerLineName('')
    setCareerLineNameEn('')
    setCareerLineStatus('')
    setIsPopup(false)
    clearErrorMessage()
  }

  const onActive = () => {
    if (id) {
      CareerLineApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('success')
          setLabelSnack(t('CAREER_LINE.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('error')
          setLabelSnack(t('CAREER_LINE.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onDelete = () => {
    if (id) {
      CareerLineApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('success')
          setLabelSnack(t('CAREER_LINE.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('error')
          setLabelSnack(t('CAREER_LINE.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { careerLineId: clId, careerLineName: clName, careerLineNameEn: clNameEn, status: clStatus } = data
    setCareerLineId(clId)
    setCareerLineName(clName)
    setCareerLineNameEn(clNameEn)
    setCareerLineStatus(clStatus)
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'careerLineName', disablePadding: false, label: t('CAREER_LINE.TABLE.CELL.CAREER_LINE_NAME') },
    { id: 'careerLineNameEn', disablePadding: false, label: t('CAREER_LINE.TABLE.CELL.CAREER_LINE_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('CAREER_LINE.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('CAREER_LINE.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { careerLineId: clId, careerLineName: clName, careerLineNameEn: clNameEn, updatedBy, updatedAt, status: clStatus } = objData
    const statusBtnActive = clStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = clStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: clId,
      id: clId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: clName },
        { option: 'TEXT', align: 'left', label: clNameEn },
        { option: 'STATUS', align: 'center', label: clStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.careerLineName)
    setNameEn(obj.careerLineNameEn)
  }

  return (
    <div className={classes.root}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`CAREER_LINE.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`CAREER_LINE.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { careerLineName: name, careerLineNameEn: nameEn && `(${nameEn})`, status: t(`CAREER_LINE.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <Card className="border-0 h-100">
        <HeaderCard text={t('CAREER_LINE.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={8}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('CAREER_LINE.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                          setPage(1)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => setIsPopup(true)} textButton={t('CAREER_LINE.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={careerLines.map((val, i) => {
                return renderData(val, i)
              })}
            />
          </div>
        </Card.Body>
      </Card>

      <PageModal
        title={careerLineId ? t('CAREER_LINE.TITLE_UPDATE') : t('CAREER_LINE.TITLE_CREATE')}
        isOpen={isPopup}
        onCancel={{ onClick: () => resetForm() }}
        onSubmit={{ onClick: () => (careerLineId ? submitEdit() : submit()) }}
        handleClose={resetForm}
        size={'XS'}
      >
        <Box className="py-2">
          {careerLineId ? (
            <Form className="mx-0 mt-0 mb-3 p-0 d-inline-flex w-100">
              <Form.Label className=" mr-auto">{t('INPUT.LABEL_STATUS')}</Form.Label>
              <SwitchButton onChange={() => setCareerLineStatus(careerLineStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={careerLineStatus === 'ACTIVE' ? true : false} />
            </Form>
          ) : (
            ''
          )}

          <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
            <TextInput
              fullWidth
              onChange={(event) => {
                setCareerLineName(event.target.value)
                clearErrorMessage()
              }}
              value={careerLineName}
              label={t('CAREER_LINE.INPUT.CAREER_LINE_NAME')}
              helperText={errorMessage.CAREER_LINE_NAME || errorMessage.IS_DUPLICATE_CAREER_LINE_NAME || errorMessage.CAREER_LINE_NAME_STRING_BASE || errorMessage.CAREER_LINE_NAME_STRING_EMPTY || errorMessage.CAREER_LINE_NAME_MAXIMUM_LENGTH || errorMessage.CAREER_LINE_NAME_ANY_REQUIRED}
              required={true}
            />
            <TextInput
              fullWidth
              onChange={(event) => {
                setCareerLineNameEn(event.target.value)
                clearErrorMessage()
              }}
              value={careerLineNameEn}
              helperText={errorMessage.CAREER_LINE_NAME_EN || errorMessage.IS_DUPLICATE_CAREER_LINE_NAME_EN || errorMessage.CAREER_LINE_NAME_EN_STRING_BASE || errorMessage.CAREER_LINE_NAME_EN_STRING_EMPTY || errorMessage.CAREER_LINE_NAME_EN_MAXIMUM_LENGTH || errorMessage.CAREER_LINE_NAME_EN_ANY_REQUIRED}
              label={t('CAREER_LINE.INPUT.CAREER_LINE_NAME_EN')}
              required={false}
            />
          </Box>
        </Box>
      </PageModal>
    </div>
  )
}
